import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAdblockStats, AdblockStats } from '../services/adblocker';
import { UserContext } from '../context/UserContext';
import Footer from '../components/Footer';

const Dashboard: React.FC = () => {
  const { user, getImmediateSubscription, loading: userContextLoading } = useContext(UserContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState<AdblockStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Get immediate subscription status from localStorage
  const immediateSubscription = getImmediateSubscription ? getImmediateSubscription() : {
    status: localStorage.getItem('subscription_status') || 'none',
    plan: localStorage.getItem('subscription_plan') || 'none'
  };

  // Use either the full user subscription object or the immediate status
  // Prioritize user data from API over localStorage (same logic as Navbar)
  const subscription = user?.subscription || immediateSubscription;
  const subscriptionStatus = subscription?.status || 'none';
  const subscriptionPlanRaw = subscription?.plan || 'none';

  // Helper function to normalize plan name (only Free or Premium)
  const normalizePlanName = (plan: any): string => {
    if (!plan || plan === 'none') {
      return 'Free';
    }
    
    // If it's an object with a name property
    if (typeof plan === 'object' && plan.name) {
      // Check if it's any premium variant
      const planName = plan.name.toLowerCase();
      if (planName.includes('premium') || planName.includes('pro') || planName.includes('basic')) {
        return 'Premium';
      }
      return plan.name;
    }
    
    // If it's a string, check if it's a premium variant
    if (typeof plan === 'string') {
      const planLower = plan.toLowerCase();
      // If it's any paid plan (BASIC, PRO, PREMIUM, etc.), consider it Premium
      if (planLower.includes('premium') || planLower.includes('pro') || planLower.includes('basic') || 
          plan === 'BASIC' || plan === 'PRO' || plan === 'PREMIUM') {
        return 'Premium';
      }
      return 'Free';
    }
    
    return 'Free';
  };

  const subscriptionPlan = normalizePlanName(subscriptionPlanRaw);
  const pageBackground = { background: 'linear-gradient(to bottom, #0c0d1f, #1b1324)' };
  
  // Check if user has premium subscription (active or trialing status)
  // Only consider it premium if we have actual user data OR confirmed localStorage
  const hasPremium = subscriptionStatus === 'active' || subscriptionStatus === 'trialing';
  
  // Redirect to payment page if user doesn't have premium
  // CRITICAL: Wait for UserContext to finish loading before making redirect decisions
  useEffect(() => {
    // Don't redirect while UserContext is still loading user data
    if (userContextLoading) {
      return;
    }
    
    // Only redirect if we're sure the user doesn't have premium
    // Check both user context and localStorage to avoid premature redirects
    const checkPremium = () => {
      // Use the subscription object (which prioritizes API data over localStorage)
      const status = subscription?.status || 'none';
      const hasPremiumStatus = status === 'active' || status === 'trialing';
      
      if (hasPremiumStatus) {
        return; // User is premium, don't redirect
      }
      
      // Fallback: double-check localStorage if subscription object doesn't have status
      const localStatus = localStorage.getItem('subscription_status');
      const hasLocalPremium = localStatus === 'active' || localStatus === 'trialing';
      
      if (hasLocalPremium) {
        return; // localStorage says premium, don't redirect
      }
      
      // Only redirect if:
      // 1. UserContext has finished loading
      // 2. Stats have finished loading
      // 3. We have confirmed the user is NOT premium
      if (!loading && !userContextLoading && !hasPremiumStatus) {
        navigate('/payment', { replace: true });
      }
    };
    
    // Small delay to allow context updates
    const timer = setTimeout(checkPremium, 500);
    return () => clearTimeout(timer);
  }, [loading, userContextLoading, subscription, navigate]);

  useEffect(() => {
    let isMounted = true;

    const fetchStats = async (isInitialLoad = false) => {
      try {
        if (isInitialLoad) {
          setLoading(true);
        }
        const data = await getAdblockStats();
        if (!isMounted) return;
        setStats(data);
        setError(null);
      } catch (err) {
        console.error('Dashboard Error:', err);
        if (!isMounted) return;
        setError('Unable to fetch tracker sync stats right now.');
      } finally {
        if (isMounted && isInitialLoad) {
          setLoading(false);
        }
      }
    };

    fetchStats(true);

    const intervalId = window.setInterval(() => {
      fetchStats(false);
    }, 20000);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchStats(false);
      }
    };

    const handleWindowFocus = () => fetchStats(false);
    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, []);

  // Show loading while UserContext is loading OR stats are loading
  if (userContextLoading || loading) {
    return (
      <div 
        className="min-h-screen flex flex-col"
        style={pageBackground}
      >
        <main className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-400"></div>
        </main>
        <Footer />
      </div>
    );
  }

  // Get status badge colors for dark theme
  const getStatusBadgeClass = () => {
    switch (subscriptionStatus) {
      case 'active':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'canceled':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'past_due':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'trialing':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={pageBackground}
    >
      <main className="flex-1">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8">
        <h1 className="text-3xl md:text-4xl font-bold mb-8 text-white">Dashboard</h1>
        
        {error && (
          <div className="mb-6 rounded-lg border border-red-400/50 bg-red-500/20 px-4 py-3 text-sm text-red-300">
            {error}
          </div>
        )}

        {/* User Subscription Info */}
        <div 
          className="rounded-lg mb-8 p-6 border border-white/10 shadow-2xl"
          style={{ backgroundColor: 'rgb(0 0 0 / 28%)' }}
        >
          <h2 className="text-xl font-semibold mb-6 text-white">User Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-white/60 mb-2">Email</p>
              <p className="font-medium text-white">{user?.email || 'N/A'}</p>
            </div>
            <div>
              <p className="text-white/60 mb-2">Subscription Plan</p>
              <div className="mt-1">
                <span className={`inline-block py-2 px-4 rounded-full text-sm font-semibold border ${getStatusBadgeClass()}`}>
                  {subscriptionPlan}
                </span>
              </div>
              {user?.subscription?.currentPeriodEnd && (
                <p className="text-sm text-white/50 mt-3">
                  {subscriptionStatus === 'canceled' ? 'Expires' : 'Renews'}: 
                  {' '}{new Date(user.subscription.currentPeriodEnd).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
        </div>

        <div 
          className="rounded-lg mb-8 border border-white/10 shadow-2xl"
          style={{ backgroundColor: 'rgb(0 0 0 / 28%)' }}
        >
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-white">AdBlocker Status</h2>
              <p className="text-sm text-white/60">Status reflects extension sync</p>
            </div>
            
            {stats && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  <div 
                    className="p-6 rounded-lg border border-white/10"
                    style={{ backgroundColor: 'rgb(0 0 0 / 28%)' }}
                  >
                    <p className="text-sm text-blue-400 font-medium mb-2">Total Ads Blocked</p>
                    <p className="text-3xl font-bold text-white">{stats.totalAdsBlocked.toLocaleString()}</p>
                  </div>
                  <div 
                    className="p-6 rounded-lg border border-white/10"
                    style={{ backgroundColor: 'rgb(0 0 0 / 28%)' }}
                  >
                    <p className="text-sm text-green-400 font-medium mb-2">Status</p>
                    <p className="text-xl font-bold text-white">
                      {stats.isEnabled ? 'Active' : 'Inactive'}
                    </p>
                  </div>
                  <div 
                    className="p-6 rounded-lg border border-white/10"
                    style={{ backgroundColor: 'rgb(0 0 0 / 28%)' }}
                  >
                    <p className="text-sm text-purple-400 font-medium mb-2">Last Updated</p>
                    <p className="text-xl font-bold text-white">
                      {stats.lastUpdated ? new Date(stats.lastUpdated).toLocaleString() : 'N/A'}
                    </p>
                  </div>
                </div>
                {!stats.lastUpdated && (
                  <div className="rounded-lg border border-yellow-400/30 bg-yellow-500/10 px-4 py-3 text-sm text-yellow-200">
                    Waiting for extension telemetry sync. Open the extension and browse briefly to generate data.
                  </div>
                )}
                <div
                  className="rounded-lg mt-6 border border-white/10"
                  style={{ backgroundColor: 'rgb(0 0 0 / 28%)' }}
                >
                  <div className="p-6">
                    <h3 className="text-lg font-semibold text-white mb-4">Top Domains (Tracker Analytics)</h3>
                    {Array.isArray(stats.domains) && stats.domains.length > 0 ? (
                      <div className="space-y-2">
                        {stats.domains.slice(0, 10).map((item) => (
                          <div
                            key={item.domain}
                            className="flex items-center justify-between rounded-md border border-white/10 px-4 py-3"
                          >
                            <p className="text-sm text-white break-all pr-4">{item.domain}</p>
                            <p className="text-sm font-semibold text-blue-300 whitespace-nowrap">
                              {item.count.toLocaleString()} blocked
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-white/60">
                        No domain analytics yet. Data appears after extension sync events.
                      </p>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Dashboard; 
